import { AppThunk } from "../appStore"
import z from "zod"
import { err, ok, Result } from "neverthrow"
import { selectFusionApiAxios } from "../selectAxios"
import { setLdng } from "../appSlice"
import { syncHttpReq, SyncHttpReqConfig } from "@fusion-advantage/formrender"
import { appError } from "../errorSlice"
import { onlineHttpError } from "./handleApiError"
import { FusionError, safeParseJson, serializeFusionError } from '@fusion-advantage/backcompat'

const ZApiDashboardApptSendReminderRespBody = z.object({
	Status: z.string(),
	sid: z.string(),
})
type ApiDashboardApptSendReminderRespBody = z.infer<typeof ZApiDashboardApptSendReminderRespBody>

export const apiDashboardApptSendReminder = (apptId: string): AppThunk<Promise<Result<ApiDashboardApptSendReminderRespBody, void>>> => async (dispatch, getState) => { 
	dispatch(setLdng({apiDashboardApptSendReminder: true}))
	const axInst = selectFusionApiAxios(getState())
	const cfg: SyncHttpReqConfig = {
		axInst,
		method: 'POST',
		url: '/api/v0/dashboard/appointment/send-reminder',
		data: {
			AppointmentID: apptId,
		},
	}
	const r = await syncHttpReq(cfg)
	dispatch(setLdng({apiDashboardApptSendReminder: false}))
	if(r.isErr()) {
		dispatch(appError(onlineHttpError(cfg, r.error, {})))
		return err()
	}
	const rParse = safeParseJson(r.value.data, ZApiDashboardApptSendReminderRespBody)
	if(rParse.isErr()) {
		dispatch(appError({
			err: serializeFusionError('apiDashboardApptSendReminder', rParse.error)
		}))
		return err()
	}
	return ok(rParse.value)
}

const ZApiDashboardGenerateClinicianVirtualJoinUrlReqBody = z.object({
	ServiceAppointmentId: z.string(),
	EncounterGuid: z.string(),
})
type ApiDashboardGenerateClinicianVirtualJoinUrlReqBody = z.infer<typeof ZApiDashboardGenerateClinicianVirtualJoinUrlReqBody>

const ZApiDashboardGenerateClinicianVirtualJoinUrlRespBody = z.object({
	ClinicianJoinUrl: z.string(),
})
type ApiDashboardGenerateClinicianVirtualJoinUrlRespBody = z.infer<typeof ZApiDashboardGenerateClinicianVirtualJoinUrlRespBody>

export const apiDashboardGenerateClinicianVirtualJoinUrl = (req: ApiDashboardGenerateClinicianVirtualJoinUrlReqBody): AppThunk<Promise<Result<ApiDashboardGenerateClinicianVirtualJoinUrlRespBody, void>>> => async (dispatch, getState) => { 
	dispatch(setLdng({apiDashboardGenerateUrls: true}))
	const axInst = selectFusionApiAxios(getState())
	const cfg: SyncHttpReqConfig = {
		axInst,
		method: 'POST',
		url: '/api/v0/conferencing/clinician-join-url',
		data: req,
	}
	const r = await syncHttpReq(cfg)
	dispatch(setLdng({apiDashboardGenerateUrls: false}))
	if(r.isErr()) {
		dispatch(appError(onlineHttpError(cfg, r.error, {})))
		return err()
	}
	const rParse = safeParseJson(r.value.data, ZApiDashboardGenerateClinicianVirtualJoinUrlRespBody)
	if(rParse.isErr()) {
		dispatch(appError({
			err: serializeFusionError('apiDashboardGenerateClinicianVirtualJoinUrl', rParse.error)
		}))
		return err()
	}
	return ok(rParse.value)
}

const ZApiDashboardGenerateVirtualJoinUrlsReqBody = z.object({
	memberGuid: z.string(),
})
type ApiDashboardGenerateVirtualJoinUrlsReqBody = z.infer<typeof ZApiDashboardGenerateVirtualJoinUrlsReqBody>

const ZApiDashboardGenerateVirtualJoinUrlsRespBody = z.object({
	// the shortened (fadv.link) member url, and the same url before shortening;
	// note the api spells this one with a capital URL
	memberJoinUrl: z.string(),
	memberLongLinkURL: z.string(),
	interpreterJoinUrl: z.string(),
	otherJoinUrl: z.string(),
	incognitoJoinUrl: z.string(),
})
type ApiDashboardGenerateVirtualJoinUrlsRespBody = z.infer<typeof ZApiDashboardGenerateVirtualJoinUrlsRespBody>

export const apiDashboardGenerateVirtualJoinUrls = (req: ApiDashboardGenerateVirtualJoinUrlsReqBody): AppThunk<Promise<Result<ApiDashboardGenerateVirtualJoinUrlsRespBody, void>>> => async (dispatch, getState) => { 
	dispatch(setLdng({apiDashboardGenerateUrls: true}))
	const axInst = selectFusionApiAxios(getState())
	const cfg: SyncHttpReqConfig = {
		axInst,
		method: 'POST',
		url: '/api/v1/conferencing/join-urls',
		data: req,
	}
	const r = await syncHttpReq(cfg)
	dispatch(setLdng({apiDashboardGenerateUrls: false}))
	if(r.isErr()) {
		// the api rejects the request when salesforce has no upcoming,
		// non-canceled telehealth appointment for the member— an expected
		// outcome rather than a failure, so say so plainly
		dispatch(appError(onlineHttpError(cfg, r.error, {
			badRequestMessage: 'No upcoming telehealth visit is scheduled for this member, so meeting links cannot be generated.  Please check the Member GUID, and that the visit is scheduled for today or later and has not been canceled.',
		})))
		return err()
	}
	const rParse = safeParseJson(r.value.data, ZApiDashboardGenerateVirtualJoinUrlsRespBody)
	if(rParse.isErr()) {
		dispatch(appError({
			err: serializeFusionError('apiDashboardGenerateVirtualJoinUrls', rParse.error)
		}))
		return err()
	}
	return ok(rParse.value)
}

const ZApiDashboardSubmitFeedbackReqBody = z.object({
	submittedDt: z.string(),
	overallExperience: z.number(), 
	easeOfNavigation: z.number(),
	productQuality: z.number(),
	whatWorked: z.string(),
	generalFeedback: z.string(),
})
type ApiDashboardSubmitFeedbackReqBody = z.infer<typeof ZApiDashboardSubmitFeedbackReqBody>

export const apiDashboardSubmitFeedback = (req: ApiDashboardSubmitFeedbackReqBody): AppThunk<Promise<Result<void, void>>> => async (dispatch, getState) => { 
	dispatch(setLdng({apiDashboardSubmitFeedback: true}))
	const axInst = selectFusionApiAxios(getState())
	const cfg: SyncHttpReqConfig = {
		axInst,
		method: 'POST',
		url: '/api/v0/dashboard/submit-feedback',
		data: req,
	}
	const r = await syncHttpReq(cfg)
	dispatch(setLdng({apiDashboardSubmitFeedback: false}))
	if(r.isErr()) {
		dispatch(appError(onlineHttpError(cfg, r.error, {})))
		return err()
	}
	return ok()
}